import {OverlayModule} from '@angular/cdk/overlay';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ErrorHandler, Injector, NgModule, inject, provideAppInitializer} from '@angular/core';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {provideAppCalendarLocale} from '@common-module/app-components/date-controls/app-calendar-locale.service';
import {GlobalErrorHandler} from '@common-module/interceptors/global-error-handler';
import {HttpErrorsInterceptor} from '@common-module/interceptors/http-errors.interceptor';
import {AppCommonEnvironmentModule, EnvironmentHelper} from '@common-module/services/environment-helper';
import {MatPaginationIntlService} from '@common-module/services/mat-pagination-intl.service';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app.routing.module';
import {AuthInterceptor} from './shared/interceptors/auth.interceptor';
import {LazyTranslateLoader} from "@common-module/translate-config/LazyTranslateLoader";
import {filter, first} from "rxjs/operators";
import {AppCustomizationService} from "@common-module/app-services/app-customization.service";
import {initTranslationService} from "../../../user-portal/src/app/shared/config/translation.config";
import {KeycloakBearerInterceptor, KeycloakService} from "keycloak-angular";
import {AuthUserService} from "@common-module/app-services/auth-user.service";
import {firstValueFrom} from "rxjs";
import {AdminPortalAuthUserService} from "./shared/service/admin-portal-auth-user.service";


export function initAppCustomization(service: AppCustomizationService) {
  return () => {
    service.init();
    return service.customization$.pipe(
      filter(customization => customization !== null),
      first()
    );
  }
}

// должен ждать когда пользователь будет залогинен и получен авторизованный пользователь и его ограничения
export function initAuthUserAndPermissions(
  authUserService: AuthUserService,
  keycloak: KeycloakService,
  env: EnvironmentHelper
) {
  return () => {
    return keycloak.init({
      config: {
        url: env.keycloakAuth.URL,
        realm: env.keycloakAuth.REALM,
        clientId: env.keycloakAuth.CLIENT_ID,
      },
      initOptions: {
        checkLoginIframe: false,
        onLoad: 'login-required',
        redirectUri: window.location.href.split('#')[0],
        pkceMethod: 'S256',
      },
      enableBearerInterceptor: true,
      bearerPrefix: 'Bearer',
    })
      .then(async (authenticated) => {
        // authenticated? залогинен ли пользователь
        if (!authenticated) {
          return true;
        }

        authUserService.init();

        await firstValueFrom(
          authUserService.currentUser$.pipe(
            filter(user => user !== null),
            first()
          )
        );

        return true;
      });
  };
}


@NgModule({
  exports: [],
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent],
  imports: [AppCommonEnvironmentModule.forRoot({
    portal: 'admin',
    keycloakClientId: 'frontend-admin',
  }),
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    OverlayModule, // for TranslateModule
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: LazyTranslateLoader
      }
    })], providers: [
    provideAppCalendarLocale(),
    {provide: AuthUserService, useClass: AdminPortalAuthUserService},
    KeycloakService,
    provideAppInitializer(() => {
      const initializerFn = (initAuthUserAndPermissions)(inject(AuthUserService), inject(KeycloakService), inject(EnvironmentHelper));
      return initializerFn();
    }),
    {provide: MatPaginatorIntl, useClass: MatPaginationIntlService},
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {provide: HTTP_INTERCEPTORS, useClass: HttpErrorsInterceptor, multi: true},
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    provideAppInitializer(() => {
      const initializerFn = (initTranslationService)(inject(TranslateService), inject(Injector));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (initAppCustomization)(inject(AppCustomizationService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ]
})
export class AppModule {
}
